import {
  Accordion,
  ActionIcon,
  Alert,
  Button,
  CopyButton,
  Group,
  Menu,
  Modal,
  SimpleGrid,
  Skeleton,
  Stack,
  Tabs,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import IconCheck from '@tabler/icons-react/IconCheck';
import IconChevronDown from '@tabler/icons-react/IconChevronDown';
import IconCopy from '@tabler/icons-react/IconCopy';
import IconDownload from '@tabler/icons-react/IconDownload';
import IconKey from '@tabler/icons-react/IconKey';
import IconLanguage from '@tabler/icons-react/IconLanguage';
import IconLogout from '@tabler/icons-react/IconLogout';
import IconRefresh from '@tabler/icons-react/IconRefresh';
import IconSettings from '@tabler/icons-react/IconSettings';
import IconUpload from '@tabler/icons-react/IconUpload';
import classes from './Header.module.scss';
import { createApiToken } from '@/api/authApi';
import { downloadLatestDesktopApp } from '@/api/desktopAppApi';
import {
  DEFAULT_QAQC_THRESHOLDS,
  QaqcThresholdKey,
  QaqcThresholds,
  getCompanySettings,
  updateCompanySettings,
} from '@/api/companySettingsApi';
import { getToken } from '@/utils/cookies';
import { isOperatorUser } from '@/utils/auth';
import { useLanguage, type Language } from '@/i18n/LanguageContext';
import { LocalizedNumberInput } from '@/components/LocalizedNumberInput/LocalizedNumberInput';
import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

const navItems = [
  { labelKey: 'nav.rigWell' as const, to: '/rig_well' },
  { labelKey: 'nav.testResults' as const, to: '/inference_results' },
  { labelKey: 'nav.centrifugeReports' as const, to: '/centrifuge_reports' },
  { labelKey: 'nav.importedFiles' as const, to: '/import_files' },
];

type ThresholdField = {
  key: QaqcThresholdKey;
  label: string;
  unit?: string;
  description?: string;
  step?: number;
  min?: number;
  max?: number;
};

type ThresholdGroup = {
  value: string;
  label: string;
  fields: ThresholdField[];
};

const thresholdGroups: ThresholdGroup[] = [
  {
    value: 'density',
    label: 'Density and phase assumptions',
    fields: [
      { key: 'base_oil_density_ppg', label: 'Base Oil Density', unit: 'ppg', step: 0.01 },
      { key: 'water_density_ppg', label: 'Water Density', unit: 'ppg', step: 0.01 },
      { key: 'hgs_sg', label: 'HGS Specific Gravity', unit: 'SG', step: 0.01 },
      { key: 'lgs_sg', label: 'LGS Specific Gravity', unit: 'SG', step: 0.01 },
      { key: 'le_bias_offset', label: 'LE Bias / Offset', unit: 'pp', step: 0.1 },
    ],
  },
  {
    value: 'le',
    label: 'Liquid phase checks',
    fields: [
      { key: 'le_low_residual_limit', label: 'LE Residual Low Limit', unit: 'pp', step: 0.1 },
      { key: 'le_high_residual_limit', label: 'LE Residual High Limit', unit: 'pp', step: 0.1 },
      { key: 'le_absolute_low', label: 'LE Absolute Low', unit: '%', step: 0.1 },
      { key: 'le_absolute_high', label: 'LE Absolute High', unit: '%', step: 0.1 },
    ],
  },
  {
    value: 'barium',
    label: 'Barium residual checks',
    fields: [
      { key: 'ba_slope_vs_mud_weight', label: 'Ba Slope vs Mud Weight', unit: '%/ppg', step: 0.1 },
      { key: 'ba_intercept_vs_mud_weight', label: 'Ba Intercept vs Mud Weight', unit: '%', step: 0.1 },
      { key: 'ba_max_clamp', label: 'Ba Max Clamp', unit: '%', step: 0.1 },
      { key: 'active_ba_residual_low_limit', label: 'Active Ba Residual Low', unit: '%', step: 0.1 },
      { key: 'active_ba_residual_high_limit', label: 'Active Ba Residual High', unit: '%', step: 0.1 },
      { key: 'effluent_ba_residual_low_limit', label: 'Effluent Ba Residual Low', unit: '%', step: 0.1 },
      { key: 'effluent_ba_residual_high_limit', label: 'Effluent Ba Residual High', unit: '%', step: 0.1 },
      { key: 'return_ba_residual_low_limit', label: 'Return/Discard Ba Residual Low', unit: '%', step: 0.1 },
      { key: 'return_ba_residual_high_limit', label: 'Return/Discard Ba Residual High', unit: '%', step: 0.1 },
    ],
  },
  {
    value: 'support',
    label: 'Barium support checks',
    fields: [
      { key: 'ba_support_low_limit', label: 'Ba Support Low', unit: 'index', step: 0.1 },
      { key: 'ba_support_low_limit_return_discard', label: 'Return/Discard Ba Support Low', unit: 'index', step: 0.1 },
      { key: 'active_ba_support_high_limit', label: 'Active Ba Support High', unit: 'index', step: 0.1 },
      { key: 'ls_effluent_ba_support_high_limit', label: 'LS Effluent Ba Support High', unit: 'index', step: 0.1 },
      { key: 'hs_effluent_ba_support_high_limit', label: 'HS Effluent Ba Support High', unit: 'index', step: 0.1 },
      { key: 'hs_suction_ba_support_high_limit', label: 'HS Suction Ba Support High', unit: 'index', step: 0.1 },
      { key: 'return_ba_support_high_limit', label: 'Return/Discard Ba Support High', unit: 'index', step: 0.1 },
      { key: 'ba_support_density_offset', label: 'Ba Support Density Offset', unit: 'ppg', step: 0.1 },
      { key: 'minimum_density_above_offset', label: 'Minimum Density Above Offset', unit: 'ppg', step: 0.1 },
    ],
  },
  {
    value: 'ba-hgs',
    label: 'Ba/HGS checks',
    fields: [
      { key: 'ba_hgs_active_low_limit', label: 'Ba/HGS Active Low', unit: 'ratio', step: 0.1 },
      { key: 'ba_hgs_active_high_limit', label: 'Ba/HGS Active High', unit: 'ratio', step: 0.1 },
      { key: 'ba_hgs_effluent_low_limit', label: 'Ba/HGS Effluent Low', unit: 'ratio', step: 0.1 },
      { key: 'ba_hgs_effluent_high_limit', label: 'Ba/HGS Effluent High', unit: 'ratio', step: 0.1 },
      { key: 'ba_hgs_hs_suction_high_limit', label: 'Ba/HGS HS Suction High', unit: 'ratio', step: 0.1 },
      { key: 'ba_hgs_return_discard_low_limit', label: 'Ba/HGS Return/Discard Low', unit: 'ratio', step: 0.1 },
      { key: 'ba_hgs_return_discard_high_limit', label: 'Ba/HGS Return/Discard High', unit: 'ratio', step: 0.1 },
      { key: 'low_hgs_denominator_limit', label: 'Low HGS Denominator Limit', unit: '%', step: 0.1 },
    ],
  },
  {
    value: 'closure',
    label: 'Closure and contradiction checks',
    fields: [
      { key: 'component_closure_ok_limit', label: 'Component Closure Warning Limit', unit: 'pp', step: 0.1 },
      { key: 'component_closure_rerun_limit', label: 'Component Closure Rerun Limit', unit: 'pp', step: 0.1 },
      { key: 'phase_closure_limit', label: 'Phase Closure Rerun Limit', unit: 'pp', step: 0.1 },
      { key: 'mud_weight_warning_limit', label: 'Mud Weight Warning Limit', unit: 'ppg', step: 0.01 },
      { key: 'mud_weight_rerun_limit', label: 'Mud Weight Rerun Limit', unit: 'ppg', step: 0.01 },
      { key: 'le_ba_le_residual_trigger', label: 'LE/Ba LE Residual Trigger', unit: 'pp', step: 0.1 },
      { key: 'le_ba_ba_residual_trigger', label: 'LE/Ba Ba Residual Trigger', unit: '%', step: 0.1 },
      {
        key: 'warning_band_fraction',
        label: 'Warning Band Fraction',
        unit: 'fraction',
        description: '0.15 means 15% of the distance between low and high limits.',
        step: 0.01,
        min: 0,
        max: 1,
      },
    ],
  },
];

const spanishThresholdGroupLabels: Record<string, string> = {
  density: 'Supuestos de densidad y fases',
  le: 'Comprobaciones de fase líquida',
  barium: 'Comprobaciones de bario residual',
  support: 'Comprobaciones de soporte de bario',
  'ba-hgs': 'Comprobaciones de Ba/HGS',
  closure: 'Comprobaciones de cierre y contradicción',
};

const spanishThresholdFieldLabels: Record<QaqcThresholdKey, string> = {
  base_oil_density_ppg: 'Densidad del aceite base',
  water_density_ppg: 'Densidad del agua',
  hgs_sg: 'Gravedad específica HGS',
  lgs_sg: 'Gravedad específica LGS',
  le_bias_offset: 'Sesgo / desplazamiento LE',
  le_low_residual_limit: 'Límite inferior residual LE',
  le_high_residual_limit: 'Límite superior residual LE',
  le_absolute_low: 'Mínimo absoluto LE',
  le_absolute_high: 'Máximo absoluto LE',
  ba_slope_vs_mud_weight: 'Pendiente Ba frente al peso del lodo',
  ba_intercept_vs_mud_weight: 'Intersección Ba frente al peso del lodo',
  ba_max_clamp: 'Límite máximo de Ba',
  active_ba_residual_low_limit: 'Residual Ba activo inferior',
  active_ba_residual_high_limit: 'Residual Ba activo superior',
  effluent_ba_residual_low_limit: 'Residual Ba de efluente inferior',
  effluent_ba_residual_high_limit: 'Residual Ba de efluente superior',
  return_ba_residual_low_limit: 'Residual Ba de retorno/descarte inferior',
  return_ba_residual_high_limit: 'Residual Ba de retorno/descarte superior',
  ba_support_low_limit: 'Soporte Ba inferior',
  ba_support_low_limit_return_discard: 'Soporte Ba de retorno/descarte inferior',
  active_ba_support_high_limit: 'Soporte Ba activo superior',
  ls_effluent_ba_support_high_limit: 'Soporte Ba de efluente LS superior',
  hs_effluent_ba_support_high_limit: 'Soporte Ba de efluente HS superior',
  hs_suction_ba_support_high_limit: 'Soporte Ba de succión HS superior',
  return_ba_support_high_limit: 'Soporte Ba de retorno/descarte superior',
  ba_support_density_offset: 'Desplazamiento de densidad para soporte Ba',
  minimum_density_above_offset: 'Densidad mínima sobre el desplazamiento',
  ba_hgs_active_low_limit: 'Ba/HGS activo inferior',
  ba_hgs_active_high_limit: 'Ba/HGS activo superior',
  ba_hgs_effluent_low_limit: 'Ba/HGS de efluente inferior',
  ba_hgs_effluent_high_limit: 'Ba/HGS de efluente superior',
  ba_hgs_hs_suction_high_limit: 'Ba/HGS de succión HS superior',
  ba_hgs_return_discard_low_limit: 'Ba/HGS de retorno/descarte inferior',
  ba_hgs_return_discard_high_limit: 'Ba/HGS de retorno/descarte superior',
  low_hgs_denominator_limit: 'Límite inferior del denominador HGS',
  component_closure_ok_limit: 'Límite de advertencia del cierre de componentes',
  component_closure_rerun_limit: 'Límite de repetición del cierre de componentes',
  phase_closure_limit: 'Límite de repetición del cierre de fases',
  mud_weight_warning_limit: 'Límite de advertencia del peso del lodo',
  mud_weight_rerun_limit: 'Límite de repetición del peso del lodo',
  le_ba_le_residual_trigger: 'Activador residual LE de LE/Ba',
  le_ba_ba_residual_trigger: 'Activador residual Ba de LE/Ba',
  warning_band_fraction: 'Fracción de banda de advertencia',
};

const thresholdFields = thresholdGroups.flatMap((group) => group.fields);

type CompanySettingsForm = {
  oil_percent_calibration: number;
  water_percent_calibration: number;
  qaqc_thresholds: QaqcThresholds;
};

const mergeQaqcThresholds = (
  thresholds?: Partial<QaqcThresholds>
): QaqcThresholds => ({
  ...DEFAULT_QAQC_THRESHOLDS,
  ...(thresholds ?? {}),
});

const numberFromInput = (value: number | string | undefined): number | null => {
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
};

const numbersEqual = (
  left: number | string | undefined,
  right: number | string | undefined
) => {
  const leftNumber = numberFromInput(left);
  const rightNumber = numberFromInput(right);

  if (leftNumber === null || rightNumber === null) {
    return left === right;
  }

  return Math.abs(leftNumber - rightNumber) < 1e-9;
};

const changedInputStyles = {
  input: {
    backgroundColor: '#fffaf0',
    borderColor: '#d89000',
    boxShadow: '0 0 0 1px rgba(216, 144, 0, 0.2)',
  },
  label: {
    color: '#6f4e00',
  },
};

const thresholdInputLabel = (field: ThresholdField, label: string) => (
  <>
    <span>{label}</span>
    {field.unit && (
      <Text component="span" size="xs" c="dimmed" ml={4}>
        [{field.unit}]
      </Text>
    )}
  </>
);

const CalibrationSettingsSkeleton = () => (
  <Stack gap="md">
    <Skeleton height={78} radius="sm" />
    <Skeleton height={78} radius="sm" />
  </Stack>
);

const QaqcThresholdsSkeleton = () => (
  <Stack gap="sm">
    <Skeleton height={18} width="38%" radius="sm" />
    <Skeleton height={16} width="76%" radius="sm" />
    <Skeleton height={46} radius="sm" />
    <Skeleton height={46} radius="sm" />
    <Skeleton height={46} radius="sm" />
  </Stack>
);

interface HeaderProps {
  logoOnly?: boolean;
}

function LanguageSwitcher() {
  const { language, setLanguage, t } = useLanguage();

  const languageOptions: Array<{
    value: Language;
    code: string;
    label: string;
  }> = [
    { value: 'en', code: 'EN', label: t('language.english') },
    { value: 'es', code: 'ES', label: t('language.spanish') },
  ];

  return (
    <Menu width={170} position="bottom-end" shadow="md">
      <Menu.Target>
        <Tooltip label={t('language.label')} withArrow>
          <button
            type="button"
            className={classes.languageButton}
            aria-label={t('language.label')}
          >
            <IconLanguage size={18} stroke={2} aria-hidden="true" />
            <span>{language.toUpperCase()}</span>
          </button>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown>
        {languageOptions.map((option) => (
          <Menu.Item
            key={option.value}
            leftSection={<Text size="xs" fw={800}>{option.code}</Text>}
            rightSection={
              language === option.value
                ? <IconCheck size={15} stroke={2} />
                : undefined
            }
            onClick={() => setLanguage(option.value)}
          >
            {option.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

function UserLanguageMenu() {
  const { language, setLanguage, t } = useLanguage();
  const languageOptions: Array<{
    value: Language;
    code: string;
    label: string;
  }> = [
    { value: 'en', code: 'EN', label: t('language.english') },
    { value: 'es', code: 'ES', label: t('language.spanish') },
  ];

  return (
    <Menu.Sub>
      <Menu.Sub.Target>
        <Menu.Sub.Item
          leftSection={<IconLanguage size={16} stroke={2} />}
          rightSection={<Text size="xs" fw={800}>{language.toUpperCase()}</Text>}
        >
          {t('language.label')}
        </Menu.Sub.Item>
      </Menu.Sub.Target>
      <Menu.Sub.Dropdown>
        {languageOptions.map((option) => (
          <Menu.Item
            key={option.value}
            leftSection={<Text size="xs" fw={800}>{option.code}</Text>}
            rightSection={
              language === option.value
                ? <IconCheck size={15} stroke={2} />
                : undefined
            }
            onClick={() => setLanguage(option.value)}
          >
            {option.label}
          </Menu.Item>
        ))}
      </Menu.Sub.Dropdown>
    </Menu.Sub>
  );
}

export function Header({ logoOnly = false }: HeaderProps) {
  const { language, t } = useLanguage();
  const location = useLocation();
  const token = getToken();
  const username = localStorage.getItem('DemoLogin') || t('common.user');
  const companyName = localStorage.getItem('company_name') || t('common.unknownCompany');
  const operatorUser = isOperatorUser();
  const visibleNavItems = operatorUser
    ? navItems.filter((item) => [
        '/rig_well',
        '/inference_results',
        '/centrifuge_reports',
      ].includes(item.to))
    : navItems;
  const [settingsOpened, setSettingsOpened] = useState(false);
  const [oilCalibration, setOilCalibration] = useState<number | string>(0);
  const [waterCalibration, setWaterCalibration] = useState<number | string>(0);
  const [qaqcThresholds, setQaqcThresholds] = useState<
    Partial<Record<QaqcThresholdKey, number | string>>
  >(DEFAULT_QAQC_THRESHOLDS);
  const [savedSettings, setSavedSettings] = useState<CompanySettingsForm | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [apiTokenModalOpened, setApiTokenModalOpened] = useState(false);
  const [apiToken, setApiToken] = useState('');
  const [apiTokenLoading, setApiTokenLoading] = useState(false);
  const [apiTokenError, setApiTokenError] = useState<string | null>(null);
  const [desktopDownloadLoading, setDesktopDownloadLoading] = useState(false);
  const [desktopDownloadError, setDesktopDownloadError] = useState<string | null>(null);

  const getThresholdFieldLabel = (field: ThresholdField) =>
    language === 'es' ? spanishThresholdFieldLabels[field.key] : field.label;

  const isTestResultsActive = location.pathname.startsWith('/calibration');
  const brandTo = logoOnly ? '/authorization' : '/';

  useEffect(() => {
    if (!settingsOpened || logoOnly) {
      return;
    }

    let active = true;

    const loadCompanySettings = async () => {
      setSettingsLoading(true);
      setSettingsError(null);

      try {
        const settings = await getCompanySettings();
        if (active) {
          const loadedSettings = {
            oil_percent_calibration: settings.oil_percent_calibration ?? 0,
            water_percent_calibration: settings.water_percent_calibration ?? 0,
            qaqc_thresholds: mergeQaqcThresholds(settings.qaqc_thresholds),
          };

          setOilCalibration(loadedSettings.oil_percent_calibration);
          setWaterCalibration(loadedSettings.water_percent_calibration);
          setQaqcThresholds(loadedSettings.qaqc_thresholds);
          setSavedSettings(loadedSettings);
        }
      } catch (error) {
        console.error('Failed to load company settings:', error);
        if (active) {
          setSettingsError(t('header.loadSettingsError'));
        }
      } finally {
        if (active) {
          setSettingsLoading(false);
        }
      }
    };

    loadCompanySettings();

    return () => {
      active = false;
    };
  }, [settingsOpened, logoOnly, t]);

  const isThresholdChanged = (key: QaqcThresholdKey) => (
    savedSettings
      ? !numbersEqual(
          qaqcThresholds[key] ?? DEFAULT_QAQC_THRESHOLDS[key],
          savedSettings.qaqc_thresholds[key]
        )
      : false
  );

  const isThresholdDefault = (key: QaqcThresholdKey) => (
    numbersEqual(
      qaqcThresholds[key] ?? DEFAULT_QAQC_THRESHOLDS[key],
      DEFAULT_QAQC_THRESHOLDS[key]
    )
  );

  const isOilCalibrationChanged = Boolean(
    savedSettings
    && !numbersEqual(oilCalibration, savedSettings.oil_percent_calibration)
  );
  const isWaterCalibrationChanged = Boolean(
    savedSettings
    && !numbersEqual(waterCalibration, savedSettings.water_percent_calibration)
  );

  const resetThresholdToDefault = (key: QaqcThresholdKey) => {
    setQaqcThresholds((current) => ({
      ...current,
      [key]: DEFAULT_QAQC_THRESHOLDS[key],
    }));
  };

  const hasSettingsChanges = Boolean(
    savedSettings
    && (
      isOilCalibrationChanged
      || isWaterCalibrationChanged
      || thresholdFields.some((field) => isThresholdChanged(field.key))
    )
  );

  const handleSaveCompanySettings = async () => {
    const getCalibrationValue = (
      value: number | string,
      label: string
    ): number | null => {
      const calibrationValue =
        typeof value === 'number'
          ? value
          : Number(value);

      if (!Number.isFinite(calibrationValue)) {
        setSettingsError(t('header.numberError', { label }));
        return null;
      }

      if (calibrationValue < -100 || calibrationValue > 100) {
        setSettingsError(t('header.rangeError', { label }));
        return null;
      }

      return calibrationValue;
    };

    const oilValue = getCalibrationValue(oilCalibration, t('calibration.oilPercent'));
    if (oilValue === null) {
      return;
    }

    const waterValue = getCalibrationValue(waterCalibration, t('calibration.waterPercent'));
    if (waterValue === null) {
      return;
    }

    const thresholdValues = mergeQaqcThresholds();
    for (const field of thresholdFields) {
      const fieldLabel = getThresholdFieldLabel(field);
      const rawValue = qaqcThresholds[field.key] ?? DEFAULT_QAQC_THRESHOLDS[field.key];
      const thresholdValue =
        typeof rawValue === 'number'
          ? rawValue
          : Number(rawValue);

      if (!Number.isFinite(thresholdValue)) {
        setSettingsError(t('header.fieldNumberError', { field: fieldLabel }));
        return;
      }

      if (field.min !== undefined && thresholdValue < field.min) {
        setSettingsError(t('header.fieldMinError', { field: fieldLabel, min: field.min }));
        return;
      }

      if (field.max !== undefined && thresholdValue > field.max) {
        setSettingsError(t('header.fieldMaxError', { field: fieldLabel, max: field.max }));
        return;
      }

      thresholdValues[field.key] = thresholdValue;
    }

    setSettingsSaving(true);
    setSettingsError(null);

    try {
      const settings = await updateCompanySettings({
        oil_percent_calibration: oilValue,
        water_percent_calibration: waterValue,
        qaqc_thresholds: thresholdValues,
      });
      const saved = {
        oil_percent_calibration: settings.oil_percent_calibration ?? 0,
        water_percent_calibration: settings.water_percent_calibration ?? 0,
        qaqc_thresholds: mergeQaqcThresholds(settings.qaqc_thresholds),
      };

      setOilCalibration(saved.oil_percent_calibration);
      setWaterCalibration(saved.water_percent_calibration);
      setQaqcThresholds(saved.qaqc_thresholds);
      setSavedSettings(saved);
      setSettingsOpened(false);
    } catch (error) {
      console.error('Failed to save company settings:', error);
      setSettingsError(t('header.saveSettingsError'));
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleCreateApiToken = async () => {
    setApiTokenModalOpened(true);
    setApiToken('');
    setApiTokenError(null);
    setApiTokenLoading(true);

    try {
      const response = await createApiToken();
      setApiToken(response.api_token);
    } catch (error) {
      console.error('Failed to create API token:', error);
      setApiTokenError(t('header.apiTokenError'));
    } finally {
      setApiTokenLoading(false);
    }
  };

  const handleDesktopAppDownload = async () => {
    setDesktopDownloadLoading(true);
    setDesktopDownloadError(null);

    try {
      await downloadLatestDesktopApp();
    } catch (error) {
      console.error('Desktop app download failed:', error);
      setDesktopDownloadError(t('header.downloadDesktopAppError'));
    } finally {
      setDesktopDownloadLoading(false);
    }
  };

  if (!token && !logoOnly) {
    return null;
  }

  return (
    <>
      <header className={classes.header}>
        <div
          className={[
            classes.headerInner,
            logoOnly ? classes.headerInnerLogoOnly : '',
          ].join(' ')}
        >
          <div className={classes.brandArea}>
            <Link
              to={brandTo}
              className={[
                classes.brandLink,
                !logoOnly && location.pathname === '/' ? classes.brandLinkActive : '',
              ].join(' ')}
              aria-label={logoOnly ? 'NPT Labs' : t('nav.home')}
            >
              <img src="/nptlabs-logo.png" alt="NPT Labs" className={classes.logo} />
            </Link>

          </div>

          {!logoOnly && (
            <nav className={classes.nav} aria-label={t('nav.primary')}>
              {visibleNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    [
                      classes.link,
                      isActive || (item.to === '/inference_results' && isTestResultsActive)
                        ? classes.linkActive
                        : '',
                    ].join(' ')
                  }
                >
                  {t(item.labelKey)}
                </NavLink>
              ))}
            </nav>
          )}

          <div className={logoOnly ? classes.logoOnlyActions : classes.actions}>
            {logoOnly && <LanguageSwitcher />}

            {!logoOnly && (
              <>
              <Tooltip
                label={
                  desktopDownloadError
                    ?? (desktopDownloadLoading
                      ? t('header.downloadingDesktopApp')
                      : t('header.downloadDesktopApp'))
                }
                color={desktopDownloadError ? 'red' : undefined}
                withArrow
              >
                <ActionIcon
                  variant="outline"
                  color="green"
                  size="lg"
                  className={classes.downloadButton}
                  aria-label={t('header.downloadDesktopApp')}
                  loading={desktopDownloadLoading}
                  onClick={handleDesktopAppDownload}
                >
                  <IconDownload size={18} stroke={2} aria-hidden="true" />
                </ActionIcon>
              </Tooltip>
              {!operatorUser && (
                <Link
                  to="/add_inference"
                  className={classes.uploadLink}
                  aria-label={t('header.uploadCsv')}
                >
                  <IconUpload size={17} stroke={2} aria-hidden="true" />
                  <span className={classes.uploadLabel}>{t('header.uploadCsv')}</span>
                </Link>
              )}
              <Menu width={220} position="bottom-end" shadow="md">
                <Menu.Target>
                  <button type="button" className={classes.userMenuButton}>
                    <span className={classes.userCard}>
                      <Text component="span" className={classes.username}>{username}</Text>
                      <Text component="span" className={classes.companyName}>{companyName}</Text>
                    </span>
                    <IconChevronDown
                      size={14}
                      stroke={2}
                      className={classes.userMenuChevron}
                      aria-hidden="true"
                    />
                  </button>
                </Menu.Target>
                <Menu.Dropdown>
                  <UserLanguageMenu />
                  {!operatorUser && (
                    <>
                      <Menu.Item
                        leftSection={<IconSettings size={16} stroke={2} />}
                        onClick={() => setSettingsOpened(true)}
                      >
                        {t('header.companySettings')}
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconKey size={16} stroke={2} />}
                        onClick={handleCreateApiToken}
                      >
                        {t('header.createApiToken')}
                      </Menu.Item>
                    </>
                  )}
                  <Menu.Divider />
                  <Menu.Item
                    component={Link}
                    to="/logout"
                    leftSection={<IconLogout size={16} stroke={2} />}
                    color="red"
                  >
                    {t('header.logout')}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
              </>
            )}
          </div>
        </div>
      </header>

      <Modal
        opened={settingsOpened}
        onClose={() => setSettingsOpened(false)}
        title={t('header.companySettings')}
        centered
        size="xl"
      >
        {settingsError && (
          <Alert color="red" mb="md">
            {settingsError}
          </Alert>
        )}

        <Tabs defaultValue="calibration">
          <Tabs.List>
            <Tabs.Tab value="calibration">{t('header.calibration')}</Tabs.Tab>
            <Tabs.Tab value="qaqc">{t('header.qc')}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="calibration" pt="md">
            {settingsLoading ? (
              <CalibrationSettingsSkeleton />
            ) : (
              <Stack gap="md">
                <LocalizedNumberInput
                  label={t('header.oilCalibration')}
                  description={t('header.oilCalibrationDescription')}
                  value={oilCalibration}
                  onChange={setOilCalibration}
                  min={-100}
                  max={100}
                  step={0.1}
                  decimalScale={2}
                  disabled={settingsSaving}
                  styles={isOilCalibrationChanged ? changedInputStyles : undefined}
                />

                <LocalizedNumberInput
                  label={t('header.waterCalibration')}
                  description={t('header.waterCalibrationDescription')}
                  value={waterCalibration}
                  onChange={setWaterCalibration}
                  min={-100}
                  max={100}
                  step={0.1}
                  decimalScale={2}
                  disabled={settingsSaving}
                  styles={isWaterCalibrationChanged ? changedInputStyles : undefined}
                />
              </Stack>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="qaqc" pt="md">
            {settingsLoading ? (
              <QaqcThresholdsSkeleton />
            ) : (
              <>
                <Stack gap="xs">
                  <Text fw={700}>{t('header.qcThresholds')}</Text>
                  <Text size="sm" c="dimmed">
                    {t('header.qcThresholdsDescription')}
                  </Text>
                </Stack>

                <Accordion multiple defaultValue={['density']} mt="sm" variant="contained">
                  {thresholdGroups.map((group) => (
                    <Accordion.Item value={group.value} key={group.value}>
                      <Accordion.Control>
                        {language === 'es'
                          ? spanishThresholdGroupLabels[group.value]
                          : group.label}
                      </Accordion.Control>
                      <Accordion.Panel>
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                          {group.fields.map((field) => {
                            const changed = isThresholdChanged(field.key);
                            const isDefault = isThresholdDefault(field.key);

                            return (
                              <LocalizedNumberInput
                                key={field.key}
                                label={thresholdInputLabel(field, getThresholdFieldLabel(field))}
                                description={
                                  field.key === 'warning_band_fraction'
                                    ? t('header.warningBandDescription')
                                    : field.description
                                }
                                value={qaqcThresholds[field.key] ?? DEFAULT_QAQC_THRESHOLDS[field.key]}
                                onChange={(value) =>
                                  setQaqcThresholds((current) => ({
                                    ...current,
                                    [field.key]: value,
                                  }))
                                }
                                min={field.min}
                                max={field.max}
                                step={field.step ?? 0.1}
                                decimalScale={3}
                                disabled={settingsSaving}
                                styles={changed ? changedInputStyles : undefined}
                                rightSection={
                                  !isDefault ? (
                                    <Tooltip label={t('header.resetDefault')} withArrow>
                                      <ActionIcon
                                        aria-label={t('header.resetFieldDefault', {
                                          field: getThresholdFieldLabel(field),
                                        })}
                                        size="sm"
                                        variant="subtle"
                                        color="gray"
                                        disabled={settingsSaving}
                                        onClick={() => resetThresholdToDefault(field.key)}
                                      >
                                        <IconRefresh size={14} stroke={2} />
                                      </ActionIcon>
                                    </Tooltip>
                                  ) : undefined
                                }
                                rightSectionPointerEvents={!isDefault ? 'all' : undefined}
                                rightSectionWidth={!isDefault ? 36 : undefined}
                              />
                            );
                          })}
                        </SimpleGrid>
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </>
            )}
          </Tabs.Panel>
        </Tabs>

        <Group justify="flex-end" mt="lg">
          <Button
            variant="default"
            onClick={() => setSettingsOpened(false)}
            disabled={settingsSaving}
          >
            {t('common.cancel')}
          </Button>
          <Button
            color="green"
            onClick={handleSaveCompanySettings}
            loading={settingsSaving}
            disabled={settingsLoading || !hasSettingsChanges}
          >
            {t('common.save')}
          </Button>
        </Group>
      </Modal>

      <Modal
        opened={apiTokenModalOpened}
        onClose={() => setApiTokenModalOpened(false)}
        title={t('header.apiTokenTitle')}
        centered
        size="lg"
      >
        {apiTokenError && (
          <Alert color="red" mb="md">
            {apiTokenError}
          </Alert>
        )}

        {apiTokenLoading ? (
          <Stack gap="sm">
            <Skeleton height={18} width="42%" radius="sm" />
            <Skeleton height={118} radius="sm" />
            <Skeleton height={36} width={128} radius="sm" />
          </Stack>
        ) : (
          apiToken && (
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                {t('header.apiTokenDescription')}
              </Text>
              <Textarea
                value={apiToken}
                readOnly
                autosize
                minRows={4}
                maxRows={8}
              />
              <Group justify="flex-end">
                <CopyButton value={apiToken}>
                  {({ copied, copy }) => (
                    <Button
                      onClick={copy}
                      color={copied ? 'green' : undefined}
                      leftSection={
                        copied
                          ? <IconCheck size={16} stroke={2} />
                          : <IconCopy size={16} stroke={2} />
                      }
                    >
                      {copied ? t('header.copied') : t('header.copyToken')}
                    </Button>
                  )}
                </CopyButton>
              </Group>
            </Stack>
          )
        )}
      </Modal>
    </>
  );
}
